import React from "react";
import { Calendar, Plus, Trash2, PackageCheck, MonitorCheck, Sparkles, RotateCcw } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";

interface TimelineDeliverablesEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addWorkDays(startDate: Date, days: number): Date {
  const cur = new Date(startDate.getTime());
  let added = 0;
  while (added < days) {
    cur.setDate(cur.getDate() + 1);
    const dayOfWeek = cur.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++;
    }
  }
  return cur;
}

function addCalDays(startDate: Date, days: number): Date {
  const cur = new Date(startDate.getTime());
  cur.setDate(cur.getDate() + days);
  return cur;
}

export const TimelineDeliverablesEditor: React.FC<TimelineDeliverablesEditorProps> = ({
  fields,
  setField
}) => {
  const deliverables = fields.deliverables || [];

  const handleUpdateDeliverable = (index: number, updates: { deliverable?: string; description?: string }) => {
    const updated = [...deliverables];
    updated[index] = { ...updated[index], ...updates };
    setField("deliverables", updated);
  };

  const handleAddDeliverable = () => {
    setField("deliverables", [
      ...deliverables,
      { deliverable: "New Deliverable", description: "Deliverable description and acceptance criteria." }
    ]);
  };

  const handleRemoveDeliverable = (index: number) => {
    if (deliverables.length <= 1) {
      alert("At least one deliverable row is required.");
      return;
    }
    const filtered = deliverables.filter((_, idx) => idx !== index);
    setField("deliverables", filtered);
  };

  const handleRecalculateDates = () => {
    const startStr = fields.testingStartDate;
    if (!startStr) {
      alert("Please select a Testing Start Date first.");
      return;
    }
    const start = new Date(startStr);
    const days = fields.consultantDays && fields.consultantDays > 0 ? fields.consultantDays : 5;
    const fieldworkEnd = addWorkDays(start, Math.max(1, days - 1));
    const draftReport = addWorkDays(fieldworkEnd, 2);
    const finalReport = addWorkDays(draftReport, 5);
    const retestEnd = addCalDays(finalReport, 30);

    setField("testingEndDate", toIso(fieldworkEnd));
    setField("draftReportDate", toIso(draftReport));
    setField("finalReportDate", toIso(finalReport));
    setField("retestingEndDate", toIso(retestEnd));
    setField("indicativeDeliveryDuration", `${days} Business Days (Fieldwork)`);
    setField("baselineAssessmentDuration", `${days} Days`);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Calendar size={16} className="text-ctb-blue" />
          Section 5: Timeline, Deliverables & PTaaS Delivery
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Schedule testing fieldwork, draft report review windows, final report issuance, and 30-day retesting validity.
        </p>
      </div>

      {/* Date Controls */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <Calendar size={13} className="text-ctb-blue" />
            Milestone Schedule (Dates)
          </label>
          <button
            type="button"
            onClick={handleRecalculateDates}
            className="text-[11px] text-ctb-blue hover:text-ctb-blue/80 font-semibold flex items-center gap-1 cursor-pointer bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-200 transition"
          >
            <Sparkles size={12} />
            Auto-Calculate Business Days
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Consultant Days (Effort)</label>
            <input
              type="number"
              min={0}
              step={0.5}
              value={fields.consultantDays ?? ""}
              onChange={(e) => setField("consultantDays", e.target.value === "" ? null : Number(e.target.value))}
              placeholder="e.g. 5"
              className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Testing Start Date</label>
            <input
              type="date"
              value={fields.testingStartDate || ""}
              onChange={(e) => setField("testingStartDate", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Fieldwork End Date</label>
            <input
              type="date"
              value={fields.testingEndDate || ""}
              onChange={(e) => setField("testingEndDate", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Draft Report Date</label>
            <input
              type="date"
              value={fields.draftReportDate || ""}
              onChange={(e) => setField("draftReportDate", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Final Report Date</label>
            <input
              type="date"
              value={fields.finalReportDate || ""}
              onChange={(e) => setField("finalReportDate", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Retesting End Date</label>
            <input
              type="date"
              value={fields.retestingEndDate || ""}
              onChange={(e) => setField("retestingEndDate", e.target.value)}
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-slate-600">Indicative Duration</label>
            <input
              type="text"
              value={fields.indicativeDeliveryDuration || ""}
              onChange={(e) => setField("indicativeDeliveryDuration", e.target.value)}
              placeholder="e.g. 5 Business Days (Fieldwork)"
              className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
            />
          </div>
        </div>
      </div>

      {/* Milestone Schedule Table */}
      {(() => {
        const schedule = fields.milestoneSchedule || [];

        const handleUpdateMilestone = (idx: number, updates: { milestone?: string; schedule?: string }) => {
          const updated = [...schedule];
          updated[idx] = { ...updated[idx], ...updates };
          setField("milestoneSchedule", updated);
        };

        const handleAddMilestone = () => {
          setField("milestoneSchedule", [
            ...schedule,
            { milestone: "New Milestone", schedule: "Schedule or duration" }
          ]);
        };

        const handleRemoveMilestone = (idx: number) => {
          if (schedule.length <= 1) {
            alert("At least one milestone row is required.");
            return;
          }
          setField("milestoneSchedule", schedule.filter((_, i) => i !== idx));
        };

        return (
          <div className="space-y-3 pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <Calendar size={13} className="text-ctb-blue" />
                  Milestone Schedule Table
                </label>
                <p className="text-[11px] text-slate-500">Every milestone row and schedule description shown in the PDF.</p>
              </div>
              <button
                type="button"
                onClick={handleAddMilestone}
                className="px-2.5 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-lg hover:bg-slate-900 flex items-center gap-1 cursor-pointer transition shadow-sm"
              >
                <Plus size={13} />
                Add Milestone
              </button>
            </div>

            <div className="space-y-2">
              {schedule.map((row, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3 hover:border-slate-300 transition"
                >
                  <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-1">
                    {idx + 1}
                  </span>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                    <div className="md:col-span-5">
                      <input
                        type="text"
                        value={row.milestone}
                        onChange={(e) => handleUpdateMilestone(idx, { milestone: e.target.value })}
                        placeholder="Milestone Name"
                        className="w-full text-xs font-semibold bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                      />
                    </div>
                    <div className="md:col-span-7">
                      <input
                        type="text"
                        value={row.schedule}
                        onChange={(e) => handleUpdateMilestone(idx, { schedule: e.target.value })}
                        placeholder="Schedule / Window"
                        className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveMilestone(idx)}
                    disabled={schedule.length <= 1}
                    className="text-slate-400 hover:text-rose-600 p-1 rounded-md transition disabled:opacity-30 cursor-pointer shrink-0 mt-1"
                    title="Remove Milestone"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Timeline Narrative */}
      {(() => {
        const defaultTimelineAssumptions =
          "This indicative timeline assumes timely provision of all required target information, access arrangements, test accounts, allowlisting, and environmental readiness by the Client, and confirmation of the date on which the platform becomes publicly available. Any material change to scope, target availability, or testing dependencies may require the timeline to be revised.";
        return (
          <div className="space-y-2 pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800">Timeline Assumptions Narrative</label>
              <button
                type="button"
                onClick={() => setField("timelineAssumptionsText", defaultTimelineAssumptions)}
                className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
              >
                <RotateCcw size={11} />
                Reset to default
              </button>
            </div>
            <textarea
              rows={3}
              value={fields.timelineAssumptionsText ?? ""}
              onChange={(e) => setField("timelineAssumptionsText", e.target.value)}
              className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
            />
          </div>
        );
      })()}

      {/* Deliverables Table */}
      <div className="space-y-3 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <PackageCheck size={13} className="text-emerald-600" />
              Deliverables (Table 2)
            </label>
            <p className="text-[11px] text-slate-500">Formal work product and engagement outputs issued to the Client.</p>
          </div>
          <button
            type="button"
            onClick={handleAddDeliverable}
            className="px-2.5 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-lg hover:bg-slate-900 flex items-center gap-1 cursor-pointer transition shadow-sm"
          >
            <Plus size={13} />
            Add Deliverable
          </button>
        </div>

        <div className="space-y-2">
          {deliverables.map((row, idx) => (
            <div
              key={idx}
              className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3 hover:border-slate-300 transition"
            >
              <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-1">
                {idx + 1}
              </span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <input
                    type="text"
                    value={row.deliverable}
                    onChange={(e) => handleUpdateDeliverable(idx, { deliverable: e.target.value })}
                    placeholder="Deliverable Title"
                    className="w-full text-xs font-semibold bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                  />
                </div>
                <div className="md:col-span-8">
                  <input
                    type="text"
                    value={row.description}
                    onChange={(e) => handleUpdateDeliverable(idx, { description: e.target.value })}
                    placeholder="Deliverable Description"
                    className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveDeliverable(idx)}
                disabled={deliverables.length <= 1}
                className="text-slate-400 hover:text-rose-600 p-1 rounded-md transition disabled:opacity-30 cursor-pointer shrink-0 mt-1"
                title="Remove Deliverable"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* PTaaS Platform Narrative */}
      {(() => {
        const defaultPtaas =
          "The Client receives access to the CTB platform from engagement commencement. The platform provides engagement visibility, a vulnerability dashboard with finding-level detail and evidence, remediation tracking, per-finding retest submission and outcomes, report artefacts, team access for relevant Client stakeholders, and centralised communication with CTB. Access remains available throughout testing, reporting, remediation coordination, and retesting, and for 90 days from the Project Completion Date, so that it covers the full retesting period during which findings are submitted for validation. Continued access after that point may require a separate agreement or subscription.\n\nUse of the CTB platform is governed by the CTB Customer Terms and Conditions incorporated into this Statement of Work. No separate platform agreement is required, and this Statement of Work takes precedence in the event of conflict.";
        return (
          <div className="space-y-2 pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <MonitorCheck size={13} className="text-ctb-blue" />
                PTaaS Platform & Remediation Workflow
              </label>
              <button
                type="button"
                onClick={() => setField("ptaasPlatformText", defaultPtaas)}
                className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
              >
                <RotateCcw size={11} />
                Reset to default
              </button>
            </div>
            <textarea
              rows={4}
              value={fields.ptaasPlatformText ?? ""}
              onChange={(e) => setField("ptaasPlatformText", e.target.value)}
              className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
            />
          </div>
        );
      })()}
    </div>
  );
};
